import React from "react";
import {
  Col,
  Row,
  Input,
  Label,
  FormFeedback,
} from "reactstrap";
import Select from "react-select";

interface RoleMenuFormProps {
  validation: any;
  menus?: any[];
  roles?: any[];
  roleMenus?: any[];
  selectedRoleId?: number | null;
  selectedMenuId?: number | null;
}

const RoleMenuForm: React.FC<RoleMenuFormProps> = ({
  validation,
  menus = [],
  roles = [],
  roleMenus = [],
  selectedRoleId = null,
  selectedMenuId = null,
}) => {
  // Filter to show only active roles
  const activeRoles = React.useMemo(() => {
    if (!Array.isArray(roles)) return [];
    return roles.filter((role: any) => 
      role.status?.toLowerCase() === "active"
    );
  }, [roles]);

  const roleOptions = activeRoles.map((role) => ({
    label: role.rolename,
    value: role.roleid,
  }));

  // Filter to show only active menus
  const activeMenus = React.useMemo(() => {
    if (!Array.isArray(menus)) return [];
    return menus.filter((menu: any) => 
      menu.status?.toLowerCase() === "active"
    );
  }, [menus]);

  // Get the selected role ID from form or prop
  const currentRoleId = React.useMemo(() => {
    return Number(validation.values.roleid || selectedRoleId) || null;
  }, [validation.values.roleid, selectedRoleId]);

  // Filter out menus that already have a combination with the selected role
  const availableMenus = React.useMemo(() => {
    if (!currentRoleId) return activeMenus;
    
    // Get menu IDs that already have a combination with the selected role
    const existingMenuIds = roleMenus
      .filter((roleMenu: any) => Number(roleMenu.roleid) === Number(currentRoleId))
      .map((roleMenu: any) => Number(roleMenu.menuid));
    
    // Filter out menus that already exist for this role
    // But keep the currently selected menu (if editing)
    return activeMenus.filter((menu: any) => {
      const menuId = Number(menu.menuid);
      const isCurrentlySelected = menuId === Number(validation.values.menuid || selectedMenuId);
      return !existingMenuIds.includes(menuId) || isCurrentlySelected;
    });
  }, [activeMenus, currentRoleId, roleMenus, validation.values.menuid, selectedMenuId]);

  const menuOptions = availableMenus.map((menu) => ({
    label: menu.title || menu.menuname,
    value: menu.menuid,
  }));

  return (
    <Row className="g-3">
      <Col md={12}>
        <Label className="fw-bold mb-1">
          Role <span className="text-danger">*</span>
        </Label>
        <div style={{ width: "100%" }}>
          <Select
            options={roleOptions}
            name="roleid"
            placeholder="Select Role"
            value={roleOptions.find((opt) => {
              const formValue = validation.values.roleid;
              const selectedValue = selectedRoleId;
              return opt.value === Number(formValue || selectedValue);
            }) || null}
            onChange={(e: { value: any }) => {
              validation.setFieldValue("roleid", e?.value);
              // Clear menuid when role changes to prevent invalid combinations
              validation.setFieldValue("menuid", "");
              validation.setFieldTouched("menuid", false);
            }}
            onBlur={() => validation.setFieldTouched("roleid", true)}
            classNamePrefix="react-select"
            className={validation.touched.roleid && validation.errors.roleid ? "react-select-error" : ""}
            isClearable
          />
        </div>
        {validation.touched.roleid && validation.errors.roleid && (
          <div className="invalid-feedback d-block">{validation.errors.roleid}</div>
        )}
      </Col>

      <Col md={12}>
        <Label className="fw-bold mb-1">
          Menu <span className="text-danger">*</span>
        </Label>
        <div style={{ width: "100%" }}>
          <Select
            options={menuOptions}
            name="menuid"
            placeholder="Select Menu"
            value={menuOptions.find((opt) => {
              const formValue = validation.values.menuid;
              const selectedValue = selectedMenuId;
              return opt.value === Number(formValue || selectedValue);
            }) || null}
            onChange={(e: { value: any }) => {
              validation.setFieldValue("menuid", e?.value);
              // Clear roleid error when menu changes to allow re-validation
              if (validation.errors.roleid && validation.touched.roleid) {
                validation.setFieldTouched("roleid", false);
              }
            }}
            onBlur={() => validation.setFieldTouched("menuid", true)}
            classNamePrefix="react-select"
            className={validation.touched.menuid && validation.errors.menuid ? "react-select-error" : ""}
            isClearable
          />
        </div>
        {validation.touched.menuid && validation.errors.menuid && (
          <div className="invalid-feedback d-block">{validation.errors.menuid}</div>
        )}
      </Col>
    </Row>
  );
};

export default RoleMenuForm;
